import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2 } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa'; // Importamos el icono de WhatsApp

const CourseModal = ({ course, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (course) {
      setIsLoading(true);
      // Simular una carga de datos o un retraso para ver el efecto
      // En una aplicación real, aquí harías tu llamada a la API
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 0); // Medio segundo de "carga"
      return () => clearTimeout(timer);
    }
  }, [course]);

  if (!course) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative"
          initial={{ scale: 0.9, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 50 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
          <motion.button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors duration-200"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X className="w-6 h-6" />
          </motion.button>

          {isLoading ? (
            <motion.div
              key="loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center h-64"
            >
              <motion.div
                className="w-16 h-16 border-4 border-t-4 border-orange-500 border-solid rounded-full animate-spin"
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              ></motion.div>
              <p className="mt-4 text-lg font-medium text-gray-600">Loading course...</p>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              {/* Título del curso destacado */}
              <motion.h2
                className="text-4xl font-extrabold text-center mb-6 pb-2 border-b-2 border-orange-200 bg-gradient-to-r from-orange-600 to-red-700 bg-clip-text text-transparent"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                {course.name}
              </motion.h2>

              <p className="text-gray-700 mb-6 leading-relaxed">
                {course.description}
              </p>

              {course.details && course.details.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    Topics Covered:
                  </h3>
                  <ul className="space-y-3">
                    {course.details.map((detail, index) => (
                      <motion.li
                        key={index}
                        className="flex items-start gap-3 text-gray-700"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * index, duration: 0.3 }}
                      >
                        <CheckCircle2 className="flex-shrink-0 w-5 h-5 text-green-500 mt-1" />
                        <span>{detail.topic}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-8 flex justify-center">
                <motion.a
                  href="https://wa.me/19795755999?text=Hello!%20I'd%20like%20more%20information%20about%20your%20courses:%20" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaWhatsapp className="w-6 h-6" />
                  I want information
                </motion.a>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CourseModal;